<template lang="html">
  <sc-flex :is-column="true" :is-full-width="true">
    <PaperFilingCompletedDialog
      :opened.sync="paperFilingCompletedDialogOpened"
      :taxReturn="taxReturn"
      :hasNextStep="hasNextStep"
      @go-to-next-step="done"
    />

    <!-- Partial: Alert -->
    <TaxReturnAlert v-if="allMailed">
      <sc-alert type="success">
        <span>
          You've completed this section.
        </span>

        <sc-button-group slot="button-group">
          <sc-button
            v-if="hasNextStep"
            type="primary"
            icon-right="arrow-right"
            @click="done()"
          >
            Next
          </sc-button>
        </sc-button-group>
      </sc-alert>
    </TaxReturnAlert>

    <sc-flex
      v-else
      class="sc-alert"
      :is-between="true"
      :is-y-centered="true"
      :gap="8"
    >
      <sc-flex :is-y-centered="true" :gap="8">
        <sc-icon
          name="warning-circle"
          size="normal"
          color="currentcolor"
        ></sc-icon>
        <sc-small :line-clamp="2">
          <b>Instruction:</b> Please read the instructions for each paper file
          returns. Once you've mailed the returns, please click the
          <b>Mark as Mailed</b> checkbox.
        </sc-small>
      </sc-flex>
    </sc-flex>

    <!-- Partial: Content -->
    <v-layout class="full-height-container">
      <div class="returns-container">
        <v-expansion-panel :expand="true" v-model="expanded">
          <v-expansion-panel-content
            v-for="(paperFiledReturn, index) of taxReturn.paper_filed_returns"
            :key="index"
          >
            <template v-slot:header>
              <v-layout class="return-header justify-space-between">
                <h2>
                  <span>{{ paperFiledReturn.custom_return_name }}</span>
                </h2>

                <v-chip
                  v-if="paperFiledReturn.is_mailed"
                  color="success"
                  small
                  class="white--text"
                >
                  Mailed
                </v-chip>
              </v-layout>
            </template>

            <v-layout class="return-content" column>
              <v-layout column>
                <div class="my-2">
                  <h3>Instructions:</h3>

                  <span class="body-1">
                    {{ paperFiledReturn.instructions }}
                  </span>
                </div>

                <div class="my-2">
                  <h3>Mailing address:</h3>

                  <span class="body-1">
                    {{ paperFiledReturn.mailing_address }}
                  </span>
                </div>
              </v-layout>

              <v-layout
                class="mt-2 return-instruction"
                align-center
                justify-center
              >
                <v-btn
                  outline
                  class="text-none px-3"
                  color="primary"
                  @click="downloadReturn(paperFiledReturn)"
                >
                  <v-icon class="mr-1">download</v-icon>
                  Download, Sign and Mail
                </v-btn>

                <v-checkbox
                  hide-details
                  v-model="paperFiledReturn.is_mailed"
                  class="mt-0 ml-2 px-2 mark-as-mailed-checkbox"
                  @change="updateReturns"
                >
                  <template slot="label">
                    <span class="ml-0" style="width: 120px"
                      >Mark as Mailed</span
                    >
                  </template>
                </v-checkbox>
              </v-layout>
            </v-layout>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-spacer style="height: 40px" />
      </div>
    </v-layout>
  </sc-flex>
</template>

<script>
import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import API from '@/api';
import { mapGetters } from 'vuex';
import UtilsMixins from '@/mixins/UtilsMixins';
import PaperFilingCompletedDialog from '@/pages/tax-return-delivery/components/PaperFilingCompletedDialog';

export default {
  mixins: [UtilsMixins],

  components: {
    PaperFilingCompletedDialog,
  },

  props: {
    companyId: {
      type: String,
      default: null,
    },

    taxReturn: {
      type: Object,
      required: true,
    },

    hasNextStep: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      expanded: [],
      paperFilingCompletedDialogOpened: false,
    };
  },

  methods: {
    isEmpty,

    async updateReturns() {
      try {
        const response = await axios.patch(
          `/tax_returns/${this.taxReturn.id}`,
          {
            company_id: this.companyId,
            paper_filed_returns: this.taxReturn.paper_filed_returns,
          },
        );

        this.$emit('tax-return-updated', response.data);
      } catch (error) {
        console.log(error);

        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem updating the return.',
          errorToSend: error,
          userEmail: this.currentUser.email,
        });
      }
    },

    expandUnmailedReturns() {
      this.expanded = this.taxReturn.paper_filed_returns.map(
        paperFiledReturn => {
          return !paperFiledReturn.is_mailed;
        },
      );
    },

    async downloadReturn(paperFiledReturn) {
      try {
        this.downloading = true;

        const response = await API.taxReturn.getDownloadLinkForPaperFiledReturn(
          {
            companyId: this.taxReturn.company_id,
            documentPath: paperFiledReturn.document_path,
          },
        );

        this.downloadDocumentByLink(response.data.link);
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading paper filed return.',
          errorToSend: error,
        });
      } finally {
        this.downloading = false;
      }
    },

    done() {
      this.$emit('finished-paper-filing');
    },
  },

  computed: {
    ...mapGetters({
      currentFirm: 'currentFirm',
    }),

    allMailed() {
      return this.taxReturn.paper_filed_returns.every(
        paperFiledReturn => paperFiledReturn.is_mailed,
      );
    },
  },

  watch: {
    allMailed() {
      if (this.allMailed) {
        this.paperFilingCompletedDialogOpened = true;
      }
    },
  },

  async created() {
    this.expandUnmailedReturns();
  },
};
</script>

<style lang="postcss" scoped>
.sc-alert {
  position: sticky;
  top: 0;

  margin: 0px;
  padding: 8px;

  background-color: var(--blue-600);
  color: var(--gray-0);
}

.full-height-container {
  overflow: auto;
  min-height: calc(100vh - 72px - 52px);
  max-height: calc(100vh - 72px - 52px);
  background-color: #f0f0f0;
  max-width: 100%;
}

.returns-container {
  flex: auto;
  min-width: 0;
  max-width: 1000px;
  margin: 0 auto;
  padding: 16px;
}

.v-expansion-panel {
  box-shadow: none;
}

.v-expansion-panel__container {
  border: 1px solid #ccc;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  background-color: #fff;
  margin: 12px 0;
  border-radius: 8px !important;
  overflow: hidden;
}

.v-expansion-panel__container {
  & >>> .v-expansion-panel__header {
    background-color: #f7f7f7;
    border-bottom: 1px solid #ccc;
  }
}

.return-header {
  padding: 8px 0px;
  font-size: 14px;
}

.return-content {
  padding: 12px 14px;
}

.mark-as-mailed-checkbox {
  flex: 0;
  padding: 5px;
  border-radius: 4px;

  & >>> .v-input--selection-controls__input .theme--light.v-icon {
    color: #888;
  }

  & >>> .v-input--selection-controls.v-input .v-label {
    display: block;
  }

  & >>> .v-input--selection-controls.v-input .v-label span {
    font-size: 14px;
  }
}
</style>
